import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { makeStyles } from '@material-ui/core/styles';
import TrendingUpRoundedIcon from '@material-ui/icons/TrendingUpRounded';
import TrendingDownRoundedIcon from '@material-ui/icons/TrendingDownRounded';
import Chart from 'react-apexcharts';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import PropTypes, { string, bool } from 'prop-types';
import numberWithCommas from '../../functions';
import chartOptions from '../../customUI/ChartOptions';
import { storeContext } from '../provider/Provider';
import Tooltip from '../tooltip/Tooltip';
import CONSTANTS from '../../utils/constants';

const InfoCard = ({
  name,
  explanation,
  value,
  percentage,
  isFetching,
  historyValues,
  labels,
  chartType,
  chartColor,
  tooltip,
  reversed,
  centered,
  inline,
  inlineTooltip,
}) => {
  const { state } = React.useContext(storeContext);
  let chartWidth = '100%';

  const lineOptions = {
    ...chartOptions,
    labels,
    tooltip: {
      x: {
        show: false,
      },
      y: {
        title: {
          formatter: () => '',
        },
      },
    },
  };
  if (chartType === 'bar') {
    chartWidth = '100%';
    lineOptions.tooltip.x.show = true;
  }
  const isLaptop = useMediaQuery({ query: '(max-width: 1950px)' });
  lineOptions.plotOptions.bar.columnWidth = '80%';
  lineOptions.plotOptions.bar.borderRadius = 0;
  lineOptions.stroke = {
    curve: 'smooth',
    width: 2,
  };
  lineOptions.xaxis = {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  };
  lineOptions.yaxis = {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  };
  lineOptions.grid = { show: false };
  lineOptions.colors = [chartColor];
  let iconColorBasedOnDirection = '#D81F27';
  if (percentage >= 0.0) {
    iconColorBasedOnDirection = '#5AA600';
  }

  let wrapperClass = {};
  if (inline) {
    wrapperClass = {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  } else {
    wrapperClass = {
      width: '100%',
    };
  }

  if (centered) {
    wrapperClass.textAlign = 'center';
  }

  const cardMinHeight = reversed && centered ? 130 : 140;

  const useStyles = makeStyles((theme) => ({
    card: {
      position: 'relative',
      background: 'white',
      borderRadius: '16px',
      padding: reversed && centered ? '10px' : '24px 24px 20px 24px',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      minHeight: inline ? 90 : cardMinHeight,
      [theme.breakpoints.down('sm')]: {
        margin: 10,
        padding: 10,
        minHeight: 0,
      },
    },
    iconUp: {
      fontSize: 'large',
      marginRight: 5,
      verticalAlign: 'middle',
      background: '#D6F3B3',
      borderRadius: '50%',
      padding: 2.5,
      fill: '#5AA600',
      [theme.breakpoints.down('sm')]: {
        fontSize: 'small',
      },
    },
    iconDown: {
      fontSize: 'large',
      marginRight: 5,
      verticalAlign: 'middle',
      background: '#F9BBC0',
      borderRadius: '50%',
      padding: 2.5,
      fill: 'D81F27',
      [theme.breakpoints.down('sm')]: {
        fontSize: 'small',
      },
    },
    percentage: {
      color: iconColorBasedOnDirection,
      fontSize: '16px',
      marginTop: 10,
      [theme.breakpoints.down('sm')]: {
        fontSize: '15px',
      },
    },
    name: {
      fontSize: reversed && centered ? 16 : 25,
      color: '#58595B',
      margin: 0,
      minHeight: reversed && centered ? 42 : 48,
      whiteSpace: inline ? 'nowrap' : 'normal',
      display: centered ? 'flex' : 'block',
      flex: inline ? 'auto 1' : 'unset',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      [theme.breakpoints.down('sm')]: {
        fontSize: 16,
        minHeight: 0,
      },
    },
    explanation: {
      fontSize: 16,
      color: '#58595B',
      margin: 0,
      minHeight: 38,
      [theme.breakpoints.down('sm')]: {
        fontSize: 15,
        minHeight: 0,
      },
    },
    explanationColor: {
      fontSize: 16,
      color: '#58595B',
    },
    value: {
      fontWeight: 600,
      fontSize: 22,
      margin: 0,
      marginTop: 5,
      fontFamily: 'Roboto',
      [theme.breakpoints.down('sm')]: {
        fontSize: 22,
      },
    },
    flex: {
      display: 'flex',
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
    },
    laptopGraph: {
      // maxWidth: '45%',
    },
    reversed: {
      display: 'flex',
      flexWrap: 'nowrap',
      flexDirection: 'column-reverse',
      alignItems: reversed === 'align-left' ? 'flex-start' : 'center',
      width: '100%',
    },
    textCentered: {
      textAlign: 'center',
    },
    wrapperClass,
  }));
  const classes = useStyles();
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });

  const renderInlineTooltip = () => {
    if (!inlineTooltip) {
      return (
        <Tooltip
          text={tooltip}
          style={{
            display: 'inline-block',
            marginLeft: 4,
            marginTop: 2,
            fontSize: '1.2em',
            // verticalAlign: 'super'
          }}
        />
      )
    }
    if (inlineTooltip) {
      return (
        <Tooltip style={{ display: 'inline-block' }} text={tooltip} />
      );
    }
    return null;
  }


  const renderPercentageChange = () => {
    if (percentage >= 0.0) {
      return (
        <Box>
          <Box 
            display="flex"
            alignItems="center"
            justifyContent="flex-start" >
            <TrendingUpRoundedIcon className={classes.iconUp} /> +
            {percentage}%{' '}
            <Tooltip
              style={{
                display: 'inline-block',
                // marginLeft: 4,
                fontSize: '1.2em',
                // verticalAlign: 'super'
              }}
              text={CONSTANTS.PercentageTooltipText}
            />
          </Box>
          {explanation ? <span className={classes.explanationColor}>{explanation}</span> : ''}
          <br />
        </Box>
      );
    }
    return (
      <Box
      >
        <Box 
          display="flex"
          alignItems="center"
          justifyContent="flex-start" 
          visibility={percentage? 'unset': 'hidden'}>
          <TrendingDownRoundedIcon className={classes.iconDown}  />{' '}
          {percentage}%{' '}
          <Tooltip
            style={{
              display: 'inline-block',
              marginLeft: 4,
              fontSize: '1.2em',
              // verticalAlign: 'super'
            }}
            text={CONSTANTS.PercentageTooltipText}
          />
        </Box>
        {explanation ? 
          <span className={percentage? classes.explanationColor: classes.explanation}>{explanation}</span> : ''}
      </Box>
    );
  }


  const renderChart = () => {
    // if (true) return null
    if (!isLaptop) {
      return (
        <div>
          {historyValues[0].data.length !== 0 && (
            <>
              {!state.websiteAnalytics.isTesting && (
                <Chart
                  options={lineOptions}
                  series={historyValues}
                  type={chartType}
                  height={isPhone ? 80 : 120}
                  width={chartWidth}
                />
              )}
            </>
          )}
        </div>
      )
    }
    return (
      <div className={classes.laptopGraph}>
        {historyValues[0].data.length !== 0 && (
          <>
            {!state.websiteAnalytics.isTesting && (
              <Chart
                options={lineOptions}
                series={historyValues}
                type={chartType}
                height={isPhone ? 100 : 120}
                width={chartWidth}
              />
            )}
          </>
        )}
      </div>
    )

  }


  return (
    <div className={classes.card}>
      <Box display='flex' flexWrap='nowrap'>
        <Box
          width={historyValues[0].data.length !== 0? '50%': '100%'}
        // className={classes.wrapperClass}
        >
          <div className={classes.name}>{name}{' '}</div>
          <Box
            width="100%"
            flexDirection={isPhone ? 'column' : 'row'}
          >
            <div className={reversed ? classes.reversed : ''}>
              <div className={classes.percentage}>
                {renderPercentageChange()}
              </div>
            </div>
            {isFetching ? (
              <CircularProgress style={{ color: '#109CF1', width: '15px', height: '15px' }} />
            ) : (
              <Box display='flex'>
                <Typography
                  className={classes.value}
                >
                  {numberWithCommas(value)}
                </Typography>
                {renderInlineTooltip()}
              </Box>
            )}
          </Box>
        </Box>
        <Box width={historyValues[0].data.length !== 0? '50%%': '00%'} maxWidth="500px">
          {renderChart()}
        </Box>
      </Box>
    </div>
  );
};

InfoCard.propTypes = {
  name: string.isRequired,
  value: string.isRequired,
  percentage: string.isRequired,
  isFetching: bool.isRequired,
  historyValues: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.arrayOf(string),
    })
  ),
  labels: PropTypes.arrayOf(string),
  explanation: string,
  chartType: string,
  chartColor: string,
  tooltip: string,
  reversed: string,
  centered: bool,
  inline: bool,
  inlineTooltip: bool,
};
InfoCard.defaultProps = {
  historyValues: [{ data: [] }],
  labels: [],
  explanation: '',
  chartType: 'line',
  chartColor: '#109CF1',
  tooltip: '-',
  reversed: '',
  centered: false,
  inline: false,
  inlineTooltip: false,
};
export default InfoCard;
